import React from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import HookApprovalCard, { hookShape } from './HookApprovalCard';
import { GENERATE_MORE } from './useCourseApproval';
import './hooks.css';

// One course's hooks, laid out for deciding: the drafts to approve or
// delete first, then the approved set in the order the app asks them
// (drag to reorder). Presentational — the actions come from
// useCourseApproval, the modal from the owner.
export default function CourseApprovalPane({
    course, hooks, busy, error, showTitle,
    onToggleApproval, onEdit, onDelete, onAdd, onGenerateMore, onDeleteRemainingDrafts, onReorderApproved,
    nextCourse, onNextCourse,
}) {
    const drafts = hooks.filter(h => h.status !== 'approved');
    const approved = hooks.filter(h => h.status === 'approved');
    const isBusy = !!busy;

    const card = (hook) => (
        <HookApprovalCard
            hook={hook}
            courseName={course.name}
            busy={isBusy}
            onToggleApproval={onToggleApproval}
            onEdit={onEdit}
            onDelete={onDelete}
        />
    );

    return (
        <div className="hk-pane">
            <div className="hk-pane-head">
                <div>
                    {showTitle && (
                        <h3 className="hk-pane-title">
                            <a href={`/editor?course=${course.id}`}>{course.name}</a>
                            {course.status && course.status !== 'published' && <span className="hk-sub"> {course.status}</span>}
                        </h3>
                    )}
                    <div className="hk-pane-counts">
                        {approved.length} approved · {drafts.length} draft{drafts.length === 1 ? '' : 's'}
                    </div>
                </div>
                <div className="hk-section-actions">
                    <button type="button" className="hk-btn hk-btn-sm hk-btn-generate" disabled={isBusy} onClick={onGenerateMore}>
                        <i className={`pi ${busy === 'generate' ? 'pi-spin pi-spinner' : 'pi-star'}`} />
                        {busy === 'generate' ? ' Generating…' : ` Generate ${GENERATE_MORE} drafts`}
                    </button>
                    <button type="button" className="hk-btn hk-btn-sm" disabled={isBusy} onClick={onAdd}>
                        <i className="pi pi-plus" /> Add hook
                    </button>
                </div>
            </div>
            {error && <div className="hk-error hk-section-error">{error}</div>}

            {hooks.length === 0 && (
                <div className="hk-empty">
                    No hooks yet. Generate drafts to get candidates, or add one by hand.
                </div>
            )}

            {drafts.length > 0 && (
                <section className="hk-pane-group">
                    <div className="hk-pane-group-head">
                        <span>Drafts to decide</span>
                        {approved.length > 0 && (
                            <button type="button" className="hk-btn hk-btn-sm hk-btn-danger" disabled={isBusy} onClick={onDeleteRemainingDrafts}>
                                <i className="pi pi-trash" /> Delete remaining drafts
                            </button>
                        )}
                    </div>
                    {drafts.map(h => <div key={h.id}>{card(h)}</div>)}
                </section>
            )}

            {approved.length > 0 && (
                <section className="hk-pane-group">
                    <div className="hk-pane-group-head">
                        <span>Approved — the order the app asks them in{approved.length > 1 ? ' (drag to reorder)' : ''}</span>
                    </div>
                    <DataTable
                        value={approved}
                        dataKey="id"
                        reorderableRows={approved.length > 1}
                        onRowReorder={(e) => onReorderApproved(e.value)}
                        className="hk-pane-table"
                    >
                        {approved.length > 1 && <Column rowReorder style={{ width: '2.5em' }} />}
                        <Column body={card} />
                    </DataTable>
                </section>
            )}

            {drafts.length === 0 && hooks.length > 0 && onNextCourse && (
                <div className="hk-pane-next">
                    {nextCourse ? (
                        <button type="button" className="hk-btn hk-btn-primary" onClick={() => onNextCourse(nextCourse)}>
                            Next course needing approval: {nextCourse.name} <i className="pi pi-arrow-right" />
                        </button>
                    ) : (
                        <span className="hk-muted">Nothing else needs approval.</span>
                    )}
                </div>
            )}
        </div>
    );
}

CourseApprovalPane.propTypes = {
    course: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, status: PropTypes.string }).isRequired,
    hooks: PropTypes.arrayOf(hookShape).isRequired,
    busy: PropTypes.string,
    error: PropTypes.string,
    showTitle: PropTypes.bool,
    onToggleApproval: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    onGenerateMore: PropTypes.func.isRequired,
    onDeleteRemainingDrafts: PropTypes.func.isRequired,
    onReorderApproved: PropTypes.func.isRequired,
    nextCourse: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    onNextCourse: PropTypes.func,
};

CourseApprovalPane.defaultProps = {
    busy: '', error: '', showTitle: true, nextCourse: null, onNextCourse: null,
};
